import { useQueries } from '@tanstack/react-query';
import { compareDesc, parseISO } from 'date-fns';
import { z } from 'zod';
import {
  listCipEnrollments,
  listEtcEnrollments,
  listHmfEnrollments,
  listHmfpEnrollments,
} from '#/components/ui/tickets/enrollments/endpoints/enrollments.ts';
import { ENROLLMENT_QUERY_NAMESPACE } from '#/components/ui/tickets/enrollments/endpoints/enrollmentsHooks.ts';
import {
  EnrollmentSchema,
  PenaltyEnrollmentSchema,
  TICKET_TYPE,
  type Enrollment,
  type EnrollmentType,
} from '#/components/ui/tickets/enrollments/model.ts';
import type { VehicleType } from '#/components/ui/tickets/compulsory-insurance-penalty/model';

const MAX_PAGE_SIZE = 1000;

const ENROLLMENT_SOURCES = [
  {
    type: TICKET_TYPE.ETC_TICKETS,
    list: listEtcEnrollments,
    schema: EnrollmentSchema,
  },
  {
    type: TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_TICKETS,
    list: listHmfEnrollments,
    schema: EnrollmentSchema,
  },
  {
    type: TICKET_TYPE.HIGHWAY_MAINTENANCE_FEE_PENALTY_TICKETS,
    list: listHmfpEnrollments,
    schema: EnrollmentSchema,
  },
  {
    type: TICKET_TYPE.COMPULSORY_INSURANCE_PENALTY_TICKETS,
    list: listCipEnrollments,
    schema: PenaltyEnrollmentSchema,
  },
] as const;

const STATE_ORDER: Record<Enrollment['state'], number> = {
  ACTIVE: 0,
  INACTIVE: 1,
};

type AccountEnrollmentRow = Enrollment & {
  type: EnrollmentType;
  vehicleType?: VehicleType;
};

const useAccountEnrollments = (accountId: string) =>
  useQueries({
    queries: ENROLLMENT_SOURCES.map(({ type, list, schema }) => ({
      queryKey: [ENROLLMENT_QUERY_NAMESPACE, 'byAccount', accountId, type],
      queryFn: async (): Promise<AccountEnrollmentRow[]> => {
        const { data } = await list({
          accountId,
          page: 1,
          pageSize: MAX_PAGE_SIZE,
        });
        return z
          .array(schema)
          .parse(data)
          .map((enrollment) => ({ ...enrollment, type }));
      },
    })),
    combine: (results) => ({
      isPending: results.some((result) => result.isPending),
      rows: results
        .flatMap((result) => result.data ?? [])
        .sort(
          (a, b) =>
            STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
            compareDesc(parseISO(a.createDateTime), parseISO(b.createDateTime)),
        ),
      errors: results.flatMap((result, index) =>
        result.error
          ? [{ type: ENROLLMENT_SOURCES[index].type, error: result.error }]
          : [],
      ),
    }),
  });

export type { AccountEnrollmentRow };
export { useAccountEnrollments };
